import React, { useState, useEffect, useRef } from 'react';
import { Box, IconButton } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import SearchResult from './LARGENAVBAR/SearchResult';
import { useDebounce } from './../../hooks/useDebounce';
import { useLocation } from 'react-router-dom';
import { getAllProducts, getProductSearch } from '../../apiCalls/urlFunction';

const sanitizeEdges = (edges) => {
  if (!Array.isArray(edges)) return [];

  return edges.map(({ node }) => {
    if (!node || typeof node !== "object") return node;

    const result = {};

    for (const key in node) {
      const value = node[key];

      if (value && Array.isArray(value.edges)) {
        result[key] = sanitizeEdges(value.edges);
      } else {
        result[key] = value;
      }
    }

    return result;
  });
};

const TYPEWRITER_PLACEHOLDER = "Search Your Favourite Brand Gift Card";

function NavbarSearch() {
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [searchResultData, setSearchResultData] = useState([]);
  const [showProductList, setShowProductList] = useState(false);
  const [resultFound, setResultFound] = useState(false);
  const [indexedProducts, setIndexedProducts] = useState([]);

  // Track if we're waiting for backend response
  const [isBackendFetching, setIsBackendFetching] = useState(false);
  const [currentSearchTerm, setCurrentSearchTerm] = useState('');

  // Typewriter placeholder state
  const [placeholderText, setPlaceholderText] = useState('');
  const [isInputFocused, setIsInputFocused] = useState(false);

  const searchDebounce = useDebounce(inputValue);

  const resultBoxRef = useRef(null);
  const inputRef = useRef(null);

  const location = useLocation();

  // Typewriter effect for placeholder
  useEffect(() => {
    if (isInputFocused || inputValue) {
      return;
    }

    let index = 0;
    let timeoutId = null;
    let isPaused = false;

    const typeNextChar = () => {
      if (isPaused) return;

      if (index <= TYPEWRITER_PLACEHOLDER.length) {
        setPlaceholderText(TYPEWRITER_PLACEHOLDER.slice(0, index));
        index += 1;
        timeoutId = setTimeout(typeNextChar, 80);
      } else {
        isPaused = true;
        timeoutId = setTimeout(() => {
          index = 0;
          isPaused = false;
          setPlaceholderText('');
          timeoutId = setTimeout(typeNextChar, 300);
        }, 2000);
      }
    };

    setPlaceholderText('');
    timeoutId = setTimeout(typeNextChar, 300);

    return () => {
      isPaused = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [isInputFocused, inputValue]);

  // Function to score product based on keyword relevance
  const scoreProduct = (product, keyword) => {
    let score = 0;

    if (product.name?.toLowerCase().startsWith(keyword))
      score += 100;

    if (product.name?.toLowerCase().includes(keyword))
      score += 80;

    if (product.slug?.toLowerCase().startsWith(keyword))
      score += 70;

    if (product.sku?.toLowerCase().startsWith(keyword))
      score += 60;

    if (product.description?.toLowerCase().includes(keyword))
      score += 20;

    if (product.region?.toLowerCase().includes(keyword))
      score += 10;

    return score;
  };

  // Local search function using indexed products (INSTANT response)
  const searchProductInPrefetchProducts = (keyword) => {
    keyword = keyword.trim().toLowerCase();

    if (!keyword) {
      setSearchResultData([]);
      setShowProductList(false);
      return;
    }

    const result = indexedProducts
      .map(item => ({
        product: item,
        score: scoreProduct(item, keyword)
      }))
      .filter(item => item.score > 0)
      .sort((a, b) => b.score - a.score)
      .map(item => item.product);

    // If there is any product matching by name/title, show only those title-matched products
    const titleMatched = result.filter(product =>
      product.name?.toLowerCase().includes(keyword)
    );

    const finalResult = titleMatched.length > 0 ? titleMatched : result;

    // Show instant results from cache
    setSearchResultData(finalResult);
    setShowProductList(true);
    setResultFound(finalResult.length === 0);
  };

  // Backend search function (SILENT background update)
  const fetchFromBackend = async (keyword) => {
    if (!keyword || keyword.trim().length < 2) return;

    setIsBackendFetching(true);
    setCurrentSearchTerm(keyword);

    try {
      const response = await getProductSearch({ q: keyword });

      if (response?.status) {
        let products =
          response?.data?.edges?.map((item) => item.node) || [];

        const keywordLower = keyword.trim().toLowerCase();
        // Apply title matching filter on backend results
        const titleMatchedBackend = products.filter(p =>
          p.name?.toLowerCase().includes(keywordLower)
        );

        if (titleMatchedBackend.length > 0) {
          products = titleMatchedBackend;
        }

        // Only update if the user hasn't typed something new
        if (currentSearchTerm === keyword) {
          // If cached results are empty, show backend results
          if (searchResultData.length === 0) {
            setSearchResultData(products);
            setResultFound(products.length === 0);
          } else {
            // Otherwise, merge/update silently (no UI flicker)
            // Check if backend returned different results
            const cacheProductIds = new Set(searchResultData.map(p => p.id));
            const newProducts = products.filter(p => !cacheProductIds.has(p.id));

            if (newProducts.length > 0) {
              // Add new products to existing results and keep title filtering logic applied
              setSearchResultData(prev => {
                const combined = [...prev, ...newProducts];
                const combinedTitleMatched = combined.filter(p =>
                  p.name?.toLowerCase().includes(keywordLower)
                );
                return combinedTitleMatched.length > 0 ? combinedTitleMatched : combined;
              });
            }
          }
        }
      }
    } catch (error) {
      console.error('Backend search error:', error);
      // Don't show error to user - keep showing cached results
    } finally {
      setIsBackendFetching(false);
    }
  };

  const getStoreAllProducts = async () => {
    try {
      const payload = {
        limit: 10000,
      };
      const res = await getAllProducts(payload);
      const sanitizedData = sanitizeEdges(res?.data?.edges);

      if (sanitizedData?.length > 0) {
        // Create search index once
        const indexed = sanitizedData.map(product => ({
          ...product,
          _search: [
            product.name,
            product.slug,
            product.sku,
            product.description,
            product.region
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase()
        }));

        setIndexedProducts(indexed);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  // Fetch products on mount
  useEffect(() => {
    getStoreAllProducts();
  }, []);

  // TRICKY SEARCH STRATEGY: Cache first, then backend
  useEffect(() => {
    if (searchDebounce?.trim()?.length > 1) {
      // STEP 1: Show INSTANT results from cache (<10ms)
      searchProductInPrefetchProducts(searchDebounce);

      // STEP 2: Silently fetch from backend in background
      fetchFromBackend(searchDebounce);

    } else {
      setShowProductList(false);
      setSearchResultData([]);
      setCurrentSearchTerm('');
    }
  }, [searchDebounce]);

  // Hide dropdown on route change
  useEffect(() => {
    setInputValue('');
    setShowProductList(false);
  }, [location.pathname]);

  // Hide dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event) {
      const clickedInsideResult =
        resultBoxRef.current &&
        resultBoxRef.current.contains(event.target);

      const clickedInsideInput =
        inputRef.current &&
        inputRef.current.contains(event.target);

      if (!clickedInsideResult && !clickedInsideInput) {
        setShowProductList(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Input change
  const handleInputChange = (e) => {
    const keyword = e.target.value;


    const isValid = /^[a-zA-Z0-9\s@#$%&*()_+\-=[\]{};:'",.<>/?\\|`~!]*$/.test(
      keyword
    );

    if (isValid && keyword.length <= 100) {
      setInputValue(keyword);
    }
  };

  // Clear input
  const handleClearInput = () => {
    setInputValue('');
    setSearchResultData([]);
    setShowProductList(false);
    setCurrentSearchTerm('');
  };

  return (
    <Box
      sx={{
        width: '100%',
        mx: 'auto',
        position: 'relative',
        mb: { xs: 2, sm: 0, md: 0, lg: 0 },

      }}
    >
      {/* Search Wrapper */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          backgroundColor: '#F6EEE5',
          border: "1px solid #E4C18F",
          borderRadius: '10px',
          pl: 2,
          pr: 0,
          py: 0,
          position: 'relative',
        }}
      >
        {/* Input */}
        <Box
          component="input"
          ref={inputRef}
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => {
            setIsInputFocused(true);
            if (searchResultData?.length > 0) {
              setShowProductList(true);
            }
          }}
          onBlur={() => {
            setIsInputFocused(false);
          }}
          placeholder={
            isInputFocused || inputValue ? '' : placeholderText
          }
          sx={{
            flex: 1,
            border: 'none',
            background: 'transparent',
            fontSize: { xs: "12px", md: "16px" },
            outline: 'none',
            fontFamily: 'Be Vietnam Pro',

            '&::placeholder': {
              color: '#000',
              opacity: 1,
              fontFamily: 'Be Vietnam Pro',
              fontSize: {
                xs: '10px',
                sm: '12px',
                md: '14px',
              },
            },
          }}
        />

        {/* Right Side Button */}
        {inputValue ? (
          <IconButton onClick={handleClearInput}>
            <ClearIcon
              sx={{
                bgcolor: "#e61414f0",
                color: "#FFF",
                width: 20,
                height: 20,
                borderRadius: 5,
                p: 0.5,
              }}
            />
          </IconButton>
        ) : (
          <Box
            sx={{
              width: 50,
              height: 42,
              // backgroundColor: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              borderTopRightRadius: '9px',
              borderBottomRightRadius: '9px',
              flexShrink: 0,
            }}
          >
            <SearchIcon
              sx={{
                color: 'primary.main',
                fontSize: 30,
              }}
            />
          </Box>
        )}


        {isBackendFetching && inputValue && (
          <Box
            sx={{
              position: 'absolute',
              right: 70,
              top: '50%',
              transform: 'translateY(-50%)',
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: '#4CAF50',
              opacity: 0.5,
              animation: 'pulse 1.5s ease-in-out infinite',
              '@keyframes pulse': {
                '0%': { opacity: 0.3 },
                '50%': { opacity: 1 },
                '100%': { opacity: 0.3 },
              },
            }}
          />
        )}
      </Box>

      {/* Search Result Dropdown */}
      <SearchResult
        loading={loading || isBackendFetching}
        showProductList={showProductList}
        resultBoxRef={resultBoxRef}
        searchResultData={searchResultData}
        resultNotFound={resultFound}
        searchTerm={inputValue}
      />
    </Box>
  );
}

export default NavbarSearch;